import React, { useState, useEffect } from "react";
import { Post } from "@/core/types/postTypes";
import { CaptionInputPanel } from "./CaptionInputPanel";
import { CaptionVariantsPanel } from "./CaptionVariantsPanel";
import { CaptionEditor } from "@/domains/Approval/CaptionEditor";
import { useCaptionGenerator, CaptionVariant } from "../hooks/useCaptionGenerator";
import { useCaptionGenerationStore, CaptionGenerationResult } from "../hooks/useCaptionGenerationStore";
import { useJobSSE } from "@/hooks/useJobSSE";
import { apiClient } from "@/services/apiClient";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { SpinnerIcon } from "@/components/icons";

export enum CaptionModalStage {
  INPUT = "input",
  GENERATING = "generating",
  VARIANTS = "variants",
  EDITING = "editing",
}

interface CaptionGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  clientId: string;
  onCaptionSaved: (updatedPost?: unknown) => void;
}

export const CaptionGeneratorModal: React.FC<CaptionGeneratorModalProps> = ({
  isOpen,
  onClose,
  post,
  clientId,
  onCaptionSaved,
}) => {
  const [stage, setStage] = useState<CaptionModalStage>(CaptionModalStage.INPUT);
  const [imageDescription, setImageDescription] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [variants, setVariants] = useState<CaptionVariant[]>([]);
  const [selectedCaptionId, setSelectedCaptionId] = useState<string | null>(null);
  const [editedCaption, setEditedCaption] = useState("");
  const [altText, setAltText] = useState("");
  const [generationId, setGenerationId] = useState<number | null>(null);
  const [canCloseDuringGeneration, setCanCloseDuringGeneration] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const { queueCaptionGeneration, saveCaption, isSaving, error } = useCaptionGenerator(
    clientId,
    post.id
  );

  const {
    startGeneration,
    completeGeneration,
    failGeneration,
    getGenerationState,
    clearGeneration,
  } = useCaptionGenerationStore();

  // Track the active job via SSE
  const { job, isCompleted, isFailed, progress, progressMessage } = useJobSSE(activeJobId);

  // Check if there's a ready generation when modal opens
  // Also fetch from backend if store is empty (handles page reload recovery)
  useEffect(() => {
    if (!isOpen) return;

    const generationState = getGenerationState(post.id);

    if (generationState?.status === "ready" && generationState.result) {
      // Use cached results from store
      setVariants(generationState.result.captions);
      setAltText(generationState.result.altText);
      setGenerationId(generationState.result.generationId ?? null);
      setStage(CaptionModalStage.VARIANTS);
    } else if (generationState?.status === "generating" && generationState.jobId) {
      // Resume tracking an in-progress generation
      setActiveJobId(generationState.jobId);
      setStage(CaptionModalStage.GENERATING);
      setCanCloseDuringGeneration(true);
    } else {
      // No store state - check backend for existing captions
      const fetchCaptionHistory = async () => {
        try {
          const response = await apiClient.get(
            `/api/client/${clientId}/posts/${post.id}/captions/history`
          );
          if (response.success && response.history && response.history.length > 0) {
            // Use the most recent generation
            const latestGen = response.history[0];
            if (latestGen.variants && latestGen.variants.length > 0) {
              const captionVariants: CaptionVariant[] = latestGen.variants.map((v: {
                id: string;
                text: string;
                characterCount: number;
              }) => ({
                id: v.id,
                text: v.text,
                characterCount: v.characterCount,
              }));

              // Update store so PostCard indicator works
              const result: CaptionGenerationResult = {
                captions: captionVariants,
                altText: latestGen.altText || "",
                generationId: latestGen.id,
              };
              completeGeneration(post.id, result);

              // Update local state
              setVariants(captionVariants);
              setAltText(latestGen.altText || "");
              setGenerationId(latestGen.id);
              setStage(CaptionModalStage.VARIANTS);
            }
          }
        } catch (err) {
          // No existing captions, stay on input stage
          console.debug("No existing captions found:", err);
        }
      };

      fetchCaptionHistory();
    }
  }, [isOpen, post.id, clientId, getGenerationState, completeGeneration]);

  // Handle job completion via SSE
  useEffect(() => {
    if (!job || !activeJobId) return;

    if (isCompleted && job.resultData) {
      const resultData = job.resultData as {
        captions?: Array<{
          id: string;
          text: string;
          characterCount: number;
        }>;
        altText?: string;
        postId?: string;
      };

      // Only process if this is for our post
      if (resultData.postId === post.id && resultData.captions) {
        const captionVariants: CaptionVariant[] = resultData.captions.map((c) => ({
          id: c.id,
          text: c.text,
          characterCount: c.characterCount,
        }));

        const result: CaptionGenerationResult = {
          captions: captionVariants,
          altText: resultData.altText || "",
        };

        setVariants(captionVariants);
        setAltText(resultData.altText || "");
        completeGeneration(post.id, result);
        setStage(CaptionModalStage.VARIANTS);
        setActiveJobId(null);
      }
    }

    if (isFailed) {
      const errorMessage = job.errorMessage || "Caption generation failed";
      failGeneration(post.id, errorMessage);
      setStage(CaptionModalStage.INPUT);
      setActiveJobId(null);
    }
  }, [job, isCompleted, isFailed, activeJobId, post.id, completeGeneration, failGeneration]);

  const handleGenerate = async (description: string, kw: string[]) => {
    setImageDescription(description);
    setKeywords(kw);
    setStage(CaptionModalStage.GENERATING);
    setCanCloseDuringGeneration(false);

    // Show the "you can close this" message after 1 second
    setTimeout(() => setCanCloseDuringGeneration(true), 1000);

    const result = await queueCaptionGeneration(description, kw);

    if (result) {
      // Job queued successfully - track it via SSE
      setActiveJobId(result.jobId);
      startGeneration(post.id, result.jobId);
    } else {
      // Failed to queue - go back to input
      setStage(CaptionModalStage.INPUT);
    }
  };

  const handleSelectVariant = (variantId: string) => {
    const variant = variants.find((v) => v.id === variantId);
    if (variant) {
      setSelectedCaptionId(variantId);
      setEditedCaption(variant.text);
      setStage(CaptionModalStage.EDITING);
    }
  };

  const handleSaveCaption = async (caption: string) => {
    const result = await saveCaption(caption, generationId, selectedCaptionId);
    if (result.success) {
      clearGeneration(post.id);
      onCaptionSaved(result.post);
      onClose();
    }
  };

  const handleClose = () => {
    // Only allow closing during generation if enough time has passed
    if (stage === CaptionModalStage.GENERATING && !canCloseDuringGeneration) {
      return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Generate AI Caption"
      size="xl"
    >
      <div className="p-6">
        {stage === CaptionModalStage.INPUT && (
          <CaptionInputPanel
            post={post}
            onGenerate={handleGenerate}
            isLoading={false}
            initialDescription={imageDescription}
            initialKeywords={keywords}
            clientId={clientId}
          />
        )}

        {stage === CaptionModalStage.GENERATING && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <SpinnerIcon className="w-12 h-12 text-purple-600" />
            <p className="text-gray-600 font-medium">
              {progressMessage || "Generating captions with AI..."}
            </p>
            {progress > 0 && (
              <div className="w-64 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            <p className="text-sm text-gray-500">This may take a few moments</p>
            {canCloseDuringGeneration && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md">
                <p className="text-sm text-blue-800 text-center">
                  You can close this modal while the caption is getting generated.
                  <br />
                  We'll show an indicator on the post when it's ready.
                </p>
              </div>
            )}
          </div>
        )}

        {stage === CaptionModalStage.VARIANTS && (
          <>
            <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                <strong>Description:</strong>{" "}
                {imageDescription.length > 60
                  ? imageDescription.slice(0, 60) + "..."
                  : imageDescription}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStage(CaptionModalStage.INPUT)}
                className="text-purple-600 hover:text-purple-700"
              >
                Edit
              </Button>
            </div>
            <CaptionVariantsPanel
              variants={variants}
              onSelect={handleSelectVariant}
              onBack={() => setStage(CaptionModalStage.INPUT)}
            />
          </>
        )}

        {stage === CaptionModalStage.EDITING && (
          <>
            <div className="mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStage(CaptionModalStage.VARIANTS)}
                className="text-purple-600 hover:text-purple-700"
              >
                &larr; See all {variants.length} variants
              </Button>
            </div>
            <CaptionEditor
              initialCaption={editedCaption}
              onSave={handleSaveCaption}
              onCancel={() => setStage(CaptionModalStage.VARIANTS)}
              isLoading={isSaving}
            />
            {altText && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <label className="text-sm font-medium text-blue-900 block mb-1">
                  Suggested Alt Text:
                </label>
                <p className="text-sm text-blue-800">{altText}</p>
              </div>
            )}
          </>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};
